import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import axios from "axios";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";

import { Form, Group, Heading } from "@peracto/peracto-ui";

import { formatDataForAPI } from "../utils";
import { useBabbageAccountConfiguration } from "../useBabbageAccountConfiguration";
import BabbageAccountConfigurationFields from "../BabbageAccountConfigurationFields";

const LogoSync = ({ setCustomLogo }) => {
  const { values } = useFormikContext();
  useEffect(() => {
    setCustomLogo(values.customLogo);
  }, [values.customLogo, setCustomLogo]);
  return null;
};

export const BulkImport = () => {
  const config = useBabbageAccountConfiguration();
  const [csvFile, setCsvFile] = useState(null);
  const [csvError, setCsvError] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [invalidAccountNumbers, setInvalidAccountNumbers] = useState([]);

  const onSubmitImport = async (formData, actions) => {
    if (!csvFile) {
      setCsvError("Please select a CSV file before submitting.");
      actions.setSubmitting(false);
      return;
    }

    setCsvError("");
    setInvalidAccountNumbers([]);
    setIsImporting(true);

    const payload = formatDataForAPI(formData, config.categoryOptions);
    const formDataObj = new FormData();
    formDataObj.append("csv", csvFile);
    formDataObj.append("configuration", JSON.stringify(payload));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/babbage-account-configuration-import`,
        formDataObj,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      toast.success("Import job created successfully");

      if (response.data?.invalidAccountNumbers?.length > 0) {
        setInvalidAccountNumbers(response.data.invalidAccountNumbers);
      }
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.hasOwnProperty("hydra:description")
          ? e.response.data["hydra:description"]
          : "There was an error creating the import job.",
      );
    } finally {
      setIsImporting(false);
      actions.setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Babbage Accounts Configuration Import | Peracto</title>
      </Helmet>
      <Heading name="Babbage Accounts Configuration Import" />

      <div className="row">
        <div className="col">
          <Group id="csv-upload" name="CSV Upload">
            <div className="mb-4">
              <label className="block mb-1 font-medium" htmlFor="csvFile">
                Main Account Numbers CSV
              </label>
              <input
                id="csvFile"
                type="file"
                accept=".csv"
                onChange={(e) => {
                  setCsvFile(e.target.files[0] || null);
                  setCsvError("");
                }}
                className="block"
                data-testid="csv-file-input"
              />
              <small className="text-gray-500 mt-1 block">
                CSV must contain a single column with header{" "}
                <code>babbage_account_number</code>
              </small>
              <a
                href="/exampleFiles/babbage_account_configuration_import_example.csv"
                download="babbage_account_configuration_import_example.csv"
                className="text-sm text-blue-600 hover:underline mt-1 inline-block"
              >
                Download Sample
              </a>
              {csvError && (
                <p
                  className="text-red-600 mt-1 text-sm"
                  data-testid="csv-error"
                >
                  {csvError}
                </p>
              )}
            </div>
          </Group>

          <Form
            autoComplete="on"
            values={{
              restrictHaynesProAccess: config.restrictHaynesProAccess,
              hideOmnihub: config.hideOmnihub,
              excludeMenuItems: config.excludeMenuItems,
              primaryColour: config.themeColours?.primaryColour,
              secondaryColour: config.themeColours?.secondaryColour,
              tertiaryColour: config.themeColours?.tertiaryColour,
              customLogo: config.customLogo,
              showStrippedFooter: config.showStrippedFooter,
              isKeyAccount: config.isKeyAccount,
              categoryBrandRestrictions: config.categoryBrandRestrictions,
              categoryBrandPriorities: config.categoryBrandPriorities,
              showOffers: config.showOffers,
              vehicleAgePopup: config.vehicleAgePopup,
            }}
            showActionPanel={false}
            onSubmit={onSubmitImport}
          >
            <LogoSync setCustomLogo={config.setCustomLogo} />
            <Group id="configuration" name="Configuration">
              <BabbageAccountConfigurationFields {...config} />
            </Group>

            {invalidAccountNumbers.length > 0 && (
              <div
                className="alert alert-warning mt-4"
                data-testid="invalid-accounts-warning"
              >
                <p className="font-bold mb-2">
                  The following account numbers were not found and will be
                  skipped:
                </p>
                <ul className="list-disc ml-4">
                  {invalidAccountNumbers.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4">
              <button
                className="btn btn-success"
                type="submit"
                data-testid="submit-import"
                disabled={isImporting}
              >
                {isImporting ? "Importing..." : "Import"}
              </button>
            </div>
          </Form>
        </div>
      </div>
    </>
  );
};
